// Entrar (documento 20, decisión D51): el correo y la contraseña, "¿Olvidaste tu contraseña?" y, abajo de una "o", los
// botones de Google, Apple y Facebook que tenga el servidor, como en la app del chofer. Con la cabecera compacta y
// márgenes justos, el botón de Google entra en la primera pantalla del celular. Quien no tiene cuenta la crea ("Creá tu cuenta"); con el
// registro cerrado, la distribuidora le da acceso con su correo. El celular con código ya no se ofrece: solo si la
// empresa lo prendió (`acceso.celular`), con un enlace aparte abajo. Abajo de todo, la entrada del preventista (D60).
import { Icono } from '@reparto/ui';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Aviso, Boton } from '../componentes/base';
import { Bienvenida } from '../componentes/Bienvenida';
import { CampoClave, CampoTexto } from '../componentes/Campos';
import { EnlaceUso } from '../componentes/EnlaceUso';
import { EntrarCon, Separador } from '../componentes/EntrarCon';
import { ErrorApi, mensajeDeError } from '../datos/api';
import { entrarConCorreo } from '../datos/cuenta';
import { aceptaRegistros, conCelular, correoLimpio, errorCorreo, proveedoresDe, textosEntrada } from '../datos/entrada';
import { catalogoAbierto } from '../datos/fuente';
import { cargarMarca, useMarca } from '../datos/marca';
import { useSesion } from '../datos/sesion';
import { leerVolver, olvidarVolver, tomarVolver } from '../datos/volver';
import { EnlacePrivacidad } from './Privacidad';

/** Lo que otras pantallas le pasan a la entrada. */
export interface EstadoEntrar {
  /** Un enlace o un proveedor que no sirvió (se dice arriba). */
  error?: string;
  origen?: 'google';
  /** Vino del carrito a confirmar (decisión D49): al entrar vuelve ahí. */
  paraConfirmar?: boolean;
  /** El correo que ya escribió en otra pantalla. */
  email?: string;
}

/** Arriba de la entrada: volver a mirar la tienda (o al carrito) y, si venía a confirmar, que lo elegido queda. */
export function ArribaEntrada({ paraConfirmar }: { paraConfirmar: boolean }) {
  const marca = useMarca((s) => s.marca);
  return (
    <>
      {catalogoAbierto(marca) ? (
        <Link
          to={paraConfirmar ? '/carrito' : '/'}
          className="-mt-2 mb-2 -ml-2 inline-flex min-h-11 items-center gap-1 rounded-full px-2 text-[15px] font-medium text-stone-700 hover:bg-stone-200/60"
        >
          <Icono nombre="chevron_left" tamano={22} />
          {paraConfirmar ? 'Volver al carrito' : 'Seguir mirando la tienda'}
        </Link>
      ) : null}
      {paraConfirmar ? (
        <Aviso tono="marca" icono="shopping_cart" className="mb-6" titulo="Entrá para confirmar tu pedido">
          Lo que elegiste queda guardado. Después de entrar volvés al carrito y, si nada cambió, se confirma solo.
        </Aviso>
      ) : null}
    </>
  );
}

export function Entrar() {
  const marca = useMarca((s) => s.marca);
  const motivo = useSesion((s) => s.motivo);
  const navegar = useNavigate();
  const { state } = useLocation() as { state: EstadoEntrar | null };
  const paraConfirmar = !!state?.paraConfirmar;
  useEffect(() => {
    if (!paraConfirmar) olvidarVolver();
  }, [paraConfirmar]);
  const [email, setEmail] = useState(state?.email ?? '');
  const [clave, setClave] = useState('');
  const [errorEmail, setErrorEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const acceso = marca?.acceso;
  const textos = textosEntrada(acceso);
  const proveedores = proveedoresDe(acceso);
  const nuevos = aceptaRegistros(acceso);
  const nombre = marca?.nombre ?? 'la distribuidora';
  // Para "Creá tu cuenta" y "¿Olvidaste…?": que no se pierda lo que venía a hacer ni el correo escrito.
  const seguir = { paraConfirmar, email: correoLimpio(email) || undefined } satisfies EstadoEntrar;

  async function entrar(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const e = errorCorreo(email);
    setErrorEmail(e);
    if (e) return;
    if (!clave) return setError('Escribí tu contraseña.');
    setOcupado(true);
    try {
      const r = await entrarConCorreo(correoLimpio(email), clave);
      // Cuenta sin negocio todavía: completa los datos (y después vuelve a donde iba).
      if (r.estado === 'falta_completar') return navegar('/registro', { replace: true });
      const v = tomarVolver();
      navegar(v?.ruta ?? '/', { replace: true, state: v ? { confirmar: v.confirmar } : undefined });
    } catch (x) {
      if (x instanceof ErrorApi && x.codigo === 'TIENDA_CERRADA') void cargarMarca();
      setError(mensajeDeError(x));
      setOcupado(false);
    }
  }

  return (
    <Bienvenida marca={marca}>
      <ArribaEntrada paraConfirmar={paraConfirmar} />
      {state?.error ? (
        <Aviso tono="peligro" icono={state.origen === 'google' ? 'no_accounts' : 'link_off'} className="mb-6">
          {state.error}
        </Aviso>
      ) : motivo === 'vencida' || motivo === 'revocada' ? (
        <Aviso tono="aviso" icono="lock_clock" className="mb-6">
          {motivo === 'revocada' ? 'Se cerró tu sesión.' : 'Pasó mucho tiempo sin entrar.'} Entrá de nuevo con tu
          cuenta.
        </Aviso>
      ) : null}

      <form onSubmit={entrar} noValidate className="flex flex-col gap-4">
        <div>
          <h2 className="text-2xl font-bold">{textos.titulo}</h2>
          <p className="mt-0.5 text-stone-600">{textos.subtitulo}</p>
        </div>
        <CampoTexto
          etiqueta="Correo"
          icono="mail"
          type="email"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="nombre@gmail.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errorEmail) setErrorEmail(null);
          }}
          error={errorEmail}
        />
        <CampoClave
          etiqueta="Contraseña"
          autoComplete="current-password"
          value={clave}
          onChange={(e) => setClave(e.target.value)}
        />
        {error ? (
          <Aviso tono="peligro" icono="error">
            <span role="alert">{error}</span>
          </Aviso>
        ) : null}
        <Boton type="submit" bloque tamano="lg" icono="login" cargando={ocupado} className="text-lg!">
          Entrar
        </Boton>
        <Link
          to="/olvide"
          state={seguir}
          className="-my-1 inline-flex min-h-11 items-center justify-center self-center rounded-full px-3 text-base font-semibold text-primary-700 underline-offset-4 hover:underline"
        >
          ¿Olvidaste tu contraseña?
        </Link>
      </form>

      {proveedores.length ? (
        <>
          <Separador className="my-4" />
          <EntrarCon proveedores={proveedores} volver={leerVolver()?.ruta ?? '/'} />
        </>
      ) : null}

      <div className="mt-8 border-t border-stone-200 pt-6 text-center">
        {nuevos ? (
          <>
            <p className="text-stone-700">¿No tenés cuenta?</p>
            <Link
              to="/crear-cuenta"
              state={seguir}
              className="mt-2 flex min-h-14 items-center justify-center gap-2 rounded-full border-2 border-primary-600 px-5 text-lg font-semibold text-primary-700 hover:bg-primary-50"
            >
              <Icono nombre="person_add" />
              Creá tu cuenta
            </Link>
          </>
        ) : (
          <p className="text-stone-700">
            ¿Sos cliente de {nombre} y todavía no tenés cuenta? Pedile a la distribuidora que te dé acceso con tu
            correo.
          </p>
        )}
        {conCelular(acceso) ? (
          <Link
            to="/entrar/celular"
            state={seguir}
            className="mt-4 inline-flex min-h-11 items-center gap-1.5 rounded-full px-3 text-[15px] font-medium text-stone-700 hover:bg-stone-200/60"
          >
            <Icono nombre="smartphone" tamano={20} />
            Entrar con el celular
          </Link>
        ) : null}
        {/* El preventista de la distribuidora, con su usuario del personal (decisión D60). */}
        <Link
          to="/vendedor/entrar"
          className="mt-4 flex min-h-11 items-center justify-center gap-1.5 rounded-full px-3 text-[15px] font-medium text-stone-700 hover:bg-stone-200/60"
        >
          <Icono nombre="badge" tamano={20} />
          ¿Sos preventista de {nombre}? Entrá como vendedor
        </Link>
      </div>
      <div className="mt-8 flex flex-col items-center gap-1 text-center">
        <EnlaceUso />
        <EnlacePrivacidad />
      </div>
    </Bienvenida>
  );
}
